import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Die Seite zu "Fotobox in der Nähe".
 *
 * Ehrlich eingeordnet: Bei dieser Anfrage zeigt Google zuerst die Karte mit
 * lokalen Betrieben, und ein Vergleichsportal ist keiner. Die Seite steht
 * auf Position 57,9 und wird das kaum grundlegend ändern.
 *
 * Nützlich kann sie trotzdem sein — für die, die hier landen: Sie erklärt,
 * was "in der Nähe" bei einer Fotobox überhaupt bedeutet, und führt zur
 * Postleitzahlsuche statt zu einer Kachelreihe.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-6 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function VorOrtInhalt({ analyse, stand }) {
  if (!analyse) return null;

  return (
    <div className="space-y-12 lg:space-y-16">
      <Abschnitt
        id="was-heisst-nah"
        titel="Was „in der Nähe“ bei einer Fotobox bedeutet"
        untertitel="Zwei Wege — und nur einer hängt an der Entfernung."
      >
        <p>
          {analyse.anzahlBoxen} Fotoboxen im Vergleich werden vom Anbieter
          geliefert und aufgebaut. Für sie zählt die Entfernung: Jeder Anbieter
          hat ein Liefergebiet, und außerhalb davon kommt entweder eine
          Anfahrtspauschale dazu oder er fährt gar nicht.
        </p>
        <p className="mt-4">
          Die übrigen {analyse.anzahlVersandBoxen} kommen{" "}
          <strong className="font-semibold text-slate-900">per Paket</strong> —
          für sie ist Entfernung kein Thema. Sie erreichen jede Adresse in
          Deutschland, und die Lieferung ist im Preis enthalten. Wer nach einer
          Fotobox „in der Nähe“ sucht, meint meist eigentlich: eine, die
          zuverlässig ankommt, ohne dass ich sie abholen muss.
        </p>
        <p className="mt-4">
          Bei {analyse.anzahlAbholung} Anbietern könnt ihr die Box auch selbst
          abholen. Das spart die Anfahrt, setzt aber ein Auto mit Platz und
          einen Termin für die Übergabe voraus.
        </p>
      </Abschnitt>

      <Abschnitt
        id="preis-vergleich"
        titel="Vor Ort oder per Versand — was kostet mehr?"
        untertitel="Der Aufbau hat seinen Preis."
      >
        <p>
          Anbieter mit Aufbau vor Ort beginnen bei {euro(analyse.min)} €, die
          Hälfte liegt unter{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.median)} €
          </strong>
          .
          {analyse.medianVersand !== null && (
            <>
              {" "}
              Versandboxen liegen im Median bei {euro(analyse.medianVersand)} €
              — {analyse.median > analyse.medianVersand
                ? `also rund ${euro(analyse.median - analyse.medianVersand)} € günstiger.`
                : "auf ähnlichem Niveau."}
            </>
          )}
        </p>
        <p className="mt-4">
          Der Unterschied ist keine Willkür: Beim Anbieter vor Ort bezahlt ihr
          Anfahrt, Aufbau, Abbau und meist jemanden, der erreichbar bleibt,
          falls etwas klemmt. Bei einer Versandbox macht ihr das selbst — dafür
          dauert es nur eine Viertelstunde.
        </p>
        {analyse.bauformen.length > 0 && (
          <>
            <h3 className="mt-8 text-lg font-semibold text-slate-900">
              Was es vor Ort gibt, was per Versand nicht
            </h3>
            <p className="mt-2">
              Bei den Anbietern vor Ort stehen{" "}
              {analyse.bauformen
                .map(([name, anzahl]) => `${anzahl}× ${name}`)
                .join(", ")}
              . Spiegelboxen, Fotokabinen und Fotobusse gibt es ausschließlich
              hier — sie sind zu groß für den Paketversand.
            </p>
          </>
        )}
      </Abschnitt>

      <Abschnitt
        id="so-findet-ihr"
        titel="So findet ihr die Anbieter in eurer Umgebung"
        untertitel="Mit der Postleitzahl statt mit einer Städteliste."
      >
        <p>
          Gebt im{" "}
          <Link
            href="/fotobox-vergleich?delivery=regional"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Vergleich
          </Link>{" "}
          eure Postleitzahl ein. Dann sehen wir nach, welche Anbieter dorthin
          liefern, wie weit sie entfernt sind und was die Anfahrt bei ihnen
          kostet — statt euch eine Liste zu zeigen, aus der die Hälfte gar
          nicht zu euch fährt.
        </p>
        <p className="mt-4">
          Findet sich niemand im Umkreis, ist das kein Rückschlag: Die
          Versandboxen kommen überall an, und ihr Aufbau ist so einfach, dass
          ihn niemand sonst übernehmen muss.
        </p>
        <p className="mt-6 text-xs text-slate-500">
          Alle Zahlen aus dem Katalog, Stand {stand}.
        </p>
      </Abschnitt>
    </div>
  );
}
